import { useState } from 'react';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Link, Navigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

const Auth = () => {
  const { t } = useI18n();
  const { user, signIn, signUp } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '' });

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const { error } = await signIn(form.email, form.password);
        if (error) throw error;
        toast.success(t('success'));
      } else {
        const { error } = await signUp(form.email, form.password, form.name);
        if (error) throw error;
        toast.success('Check your email to confirm your account!');
      }
    } catch (err: any) {
      toast.error(err.message || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center">
      <div className="w-full max-w-md mx-4">
        <div className="glass-panel border-0 rounded-2xl p-8 animate-fade-in-up">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-full gradient-accent flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-5 w-5 text-primary-foreground" />
            </div>
            <h1 className="font-display text-2xl font-bold text-foreground">
              {isLogin ? t('loginTitle') : t('registerTitle')}
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label className="text-foreground">{t('fullName')}</Label>
                <Input
                  required
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="glass-input"
                />
              </div>
            )}
            <div>
              <Label className="text-foreground">{t('email')}</Label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="glass-input"
              />
            </div>
            <div>
              <Label className="text-foreground">{t('password')}</Label>
              <Input
                required
                type="password"
                minLength={6}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                className="glass-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full gradient-accent text-primary-foreground shadow-glow"
            >
              {isLogin ? t('signIn') : t('signUp')}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {isLogin ? t('noAccount') : t('hasAccount')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
